import { useCallback } from "react";
import { useFormContext } from "react-hook-form";

export const resolveTargetId = (
  target?: string,
  cols?: Record<string, any>,
) => {
  if (!target || !cols) return undefined;

  const targetEntry = Object.entries(cols).find(
    ([, col]: [string, any]) => col?.id === target,
  );

  return targetEntry?.[0];
};

export const resolveTargetIdInSection = (
  target?: string,
  rows?: Array<{ cols?: Record<string, any> }>,
) => {
  if (!target || !rows) return undefined;

  for (const row of rows) {
    const match = resolveTargetId(target, row.cols);
    if (match) return match;
  }

  return undefined;
};

export const hasFeature = (
  feature: string | string[] | undefined,
  key: string,
) => {
  if (!feature) return false;
  return Array.isArray(feature) ? feature.includes(key) : feature === key;
};

export const resolveTargetIdInSections = (
  target?: string,
  sections?: any[],
): string | undefined => {
  if (!target || !sections) return undefined;

  for (const section of sections) {
    if (!section) continue;

    if (section.shared && section.sections) {
      const match = resolveTargetIdInSections(target, section.sections);
      if (match) return match;
    }

    const rowsMatch = resolveTargetIdInSection(target, section.rows);
    if (rowsMatch) return rowsMatch;

    for (const row of section.rows ?? []) {
      const subrowMatch = resolveTargetIdInSection(target, row.subrows);
      if (subrowMatch) return subrowMatch;
    }

    for (const subSection of section.subSections ?? []) {
      for (const row of subSection.rows ?? []) {
        const items = Object.values(row.items ?? {}) as Array<{
          cols?: Record<string, any>;
        }>;
        for (const item of items) {
          const match = resolveTargetId(target, item.cols);
          if (match) return match;
        }
      }
    }
  }

  return undefined;
};

export const resolveCopyValTarget = (
  feature?: string | string[],
  target?: string,
  cols?: Record<string, any>,
  sections?: any[],
) => {
  if (!hasFeature(feature, "copyVal")) return undefined;
  return (
    resolveTargetId(target, cols) ?? resolveTargetIdInSections(target, sections)
  );
};

export const useCopyVal = () => {
  const { setValue } = useFormContext();

  const applyCopyVal = useCallback(
    ({
      feature,
      targetField,
      value,
    }: {
      feature?: string | string[];
      targetField?: string;
      value: any;
    }) => {
      if (!hasFeature(feature, "copyVal") || !targetField) return;
      setValue(targetField, value);
    },
    [setValue],
  );

  return {
    applyCopyVal,
  };
};
